/* ============================================================
   Seller pricing (TASK-179) — mirrors backend PricingDtos
   ============================================================ */

export interface PricingMaterialRule {
  materialId: string;
  pricePerGramRub: number | null;
  isAllowed: boolean;
}

export interface QuantityDiscountTier {
  minCopies: number;
  discountPercent: number;
}

/* ── 00a0da87: post-processing pricing options (mirrors PricingDtos) ── */

/** One performer-defined priced option (≤20, unique names, 1–100 chars). */
export interface ProviderPricingOption {
  name: string;
  pricePerCopyRub: number;
}

/** Post-processing offers: paint by cm², support removal by gram, options. */
export interface ProviderPostProcessingOptions {
  offersPainting: boolean;
  paintPricePerCm2Rub: number;
  paintMinimumRub: number;
  offersSupportRemoval: boolean;
  supportRemovalPricePerGramRub: number;
  supportRemovalMinimumRub: number;
  options: ProviderPricingOption[];
}

/** Null-safe default — null means «not configured», all offers off. */
export const defaultPostProcessingOptions: ProviderPostProcessingOptions = {
  offersPainting: false,
  paintPricePerCm2Rub: 0,
  paintMinimumRub: 0,
  offersSupportRemoval: false,
  supportRemovalPricePerGramRub: 0,
  supportRemovalMinimumRub: 0,
  options: [],
};

export interface ProviderPricingSettings {
  machineHourlyRateRub: number;
  depreciationPerHourRub: number;
  maintenancePerHourRub: number;
  electricityPerHourRub: number;
  setupFeeRub: number;
  laborPostProcessingRub: number;
  failureBufferPercent: number;
  targetMarginPercent: number;
  rushMultiplier: number;
  minimumOrderRub: number;
  minCopies: number;
  maxCopies: number | null;
  minWeightGrams: number;
  maxWeightGrams: number;
  minDimensionMm: number;
  maxDimensionMm: number;
  maxPrintMinutes: number;
  materialRules: PricingMaterialRule[];
  discountTiers: QuantityDiscountTier[];
  /** 00a0da87: null = not configured (all offers off) — legacy-safe. */
  postProcessing?: ProviderPostProcessingOptions | null;
}

/** Default form state (mirrors backend safe defaults). */
export const defaultPricingSettings: ProviderPricingSettings = {
  machineHourlyRateRub: 300,
  depreciationPerHourRub: 50,
  maintenancePerHourRub: 10,
  electricityPerHourRub: 5,
  setupFeeRub: 100,
  laborPostProcessingRub: 0,
  failureBufferPercent: 10,
  targetMarginPercent: 30,
  rushMultiplier: 1.5,
  minimumOrderRub: 300,
  minCopies: 1,
  maxCopies: 100,
  minWeightGrams: 0,
  maxWeightGrams: 20000,
  minDimensionMm: 0,
  maxDimensionMm: 350,
  maxPrintMinutes: 2880,
  materialRules: [],
  discountTiers: [],
  postProcessing: null,
};

export interface SellerQuotePreviewRequest {
  providerId: string;
  materialId?: string;
  volumeMm3: number;
  dimX: number;
  dimY: number;
  dimZ: number;
  infillPercent?: number;
  copies?: number;
  isRush?: boolean;
  slicerGrams?: number | null;
  slicerMinutes?: number | null;
  /* 00a0da87: customer toggles ride the preview so the estimate includes
   * the same post-processing chain as the quote flow. 7f5734fc:
   * supportGramsIsEstimate marks a heuristic mass (vs slicer-measured). */
  includePaint?: boolean;
  includeSupportRemoval?: boolean;
  supportGramsIsEstimate?: boolean;
}

export interface SellerQuotePreview {
  setupFee: number;
  materialCost: number;
  machineCost: number;
  laborCost: number;
  wasteBufferCost: number;
  marginCost: number;
  discountValue: number;
  rushSurcharge: number;
  sellerPrice: number;
  platformFee: number;
  customerTotal: number;
  discountPercent: number;
  copies: number;
  estimatedGrams: number;
  estimatedMinutes: number;
  slicerDataUsed: boolean;
  /** 00a0da87: post-processing breakdown (null when nothing was toggled/offered). */
  postProcessing?: PostProcessingQuote | null;
}

/** One priced line of the post-processing part (unknown price → chat). */
export interface PostProcessingQuoteLine {
  name: string;
  cost: number;
  /** False when the price cannot be auto-calculated → «уточняется в чате». */
  priceKnown: boolean;
}

/** Post-processing breakdown with honest «price unknown» flags. */
export interface PostProcessingQuote {
  paintCost: number;
  paintApplied: boolean;
  paintPriceKnown: boolean;
  supportRemovalCost: number;
  supportRemovalApplied: boolean;
  supportRemovalPriced: boolean;
  supportGramsUsed: number;
  /** 7f5734fc: true → supportGramsUsed is an analyzer estimate («оценка»). */
  supportGramsIsEstimate?: boolean;
  optionsCost: number;
  optionLines: PostProcessingQuoteLine[];
}

/* ============================================================
   Provider reviews (TASK-179) — mirrors backend ReviewDto
   ============================================================ */

export type ReviewStatus = 'Published' | 'Hidden' | 'Removed' | number;

export interface Review {
  id: string;
  providerId: string;
  customerName: string;
  rating: number;
  comment: string;
  createdAtUtc: string;
  status: ReviewStatus;
  reportCount: number;
}
